<template>
  <transition name="fade">
    <div v-if="show" class="sanitas-search">
      <div class="closeIcon" @click="show = false">
        <dotcom-icon
          name="xmark"
          art="light"
          color="--c-primary-neutral-3"
          hover-color="--c-primary-neutral-3"
        />
      </div>
      <div class="sanitas-search__form">
        <div class="search-container">
          <h2 class="title">{{ $t('title') }}</h2>

          <p class="instructions">{{ resultsFoundMessage }}</p>

          <sanitas-form-search
            class="sanitas-form-search"
            is-negative
            :autocomplete-search-endpoint-url="autoCompleteUrl"
            query-search-endpoint-method="GET"
            :minimum-search-length="3"
            :search-value="currentSearchValue"
            :should-show-results="shouldShowResults"
            :max-visible-suggestions="maxVisibleSuggestions"
            @sanitas_search_search="userDidSearch"
            @suggestionsOpened="val => (suggestionsOpened = val)"
          >
            <slot></slot>
          </sanitas-form-search>
          <template v-if="hasError">
            <p class="error">{{ $t('search-error-message') }}</p>
          </template>
        </div>
        <template v-if="shouldShowResults">
          <sanitas-search-list
            :class="{ 'is-open': suggestionsOpened }"
            :results="searchResults"
          >
          </sanitas-search-list>
        </template>
        <loading v-if="showLoading"></loading>
      </div>
    </div>
  </transition>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'

import { client } from '@/helpers/http'

import Loading from './components/Loading'
import SanitasFormSearch from './components/SanitasFormSearch'
import SanitasSearchList from './components/SanitasSearchList'

export default {
  name: 'SanitasSearchModule',
  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
    SanitasSearchList,
    SanitasFormSearch,
    Loading,
  },
  props: {
    language: {
      type: String,
      default: 'de',
    },
    /**
     * Array of retention Related Terms
     * [ 'String_1', 'String_2', 'String_N' ]
     *
     */
    retentionRelatedTerms: {
      type: Array,
      default: () => [],
    },
    /**
     * url that will be queried in order to get the auto-complete suggestions.
     *
     * The endpoint response has to be an array of `String`:
     * ```
     * [ 'String_1', 'String_2', 'String_N' ]
     * ```
     */
    autoCompleteUrl: {
      type: String,
      default: '',
    },

    /**
     * url that will be queried in order to get the search results.
     *
     * The endpoint response needs to have the structure detailed in
     * the `sanitas-search-list` Polymer component
     */
    searchUrl: {
      type: String,
      default: '',
    },

    /**
     * Maximum number of visible suggestions in the dropdown
     */
    maxVisibleSuggestions: {
      type: Number,
      default: 3,
    },
  },

  setup(props) {
    const loadLabels = lang => import(`./i18n/${lang}.json`)
    provideI36n(props.language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },

  data: () => ({
    currentSearchValue: '',
    searchTerm: '',
    hasError: false,
    showLoading: false,
    suggestionsOpened: false,
    show: false,
    searchResults: { empty: true },
  }),

  computed: {
    shouldShowResults() {
      return (
        this.searchResults !== undefined && this.searchResults.empty !== true
      )
    },
    resultsFoundMessage() {
      const { searchResults, searchTerm } = this
      const isUserSearching =
        searchResults === undefined || searchResults.empty === true
      if (isUserSearching) {
        return this.$t('instructions')
      }

      let numResults = 0
      const sections = ['articles', 'documents', 'products']

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i]
        const sectionResults = searchResults[section]

        if (sectionResults) {
          numResults += sectionResults.length
        }
      }
      let message =
        numResults > 1
          ? 'results-found-message-plural'
          : 'results-found-message-singular'
      return this.$t(message, { numResults, searchTerm })
    },
  },
  watch: {
    show(value) {
      document.body.style.overflow = value ? 'hidden' : 'auto'
      this.searchResults = { empty: true }
      this.showLoading = false
    },
  },
  created() {
    window.addEventListener('sanitas-open-search-view', () => {
      this.show = true
    })
  },

  methods: {
    userDidSearch({ value, suggestions }) {
      this.searchTerm = value
      if (value === undefined) {
        return
      }
      let retental = -1
      if (this.retentionRelatedTerms) {
        this.retentionRelatedTerms.some(function (el, i) {
          if (el.toLowerCase() === value.toLowerCase()) {
            retental = i
            return true
          }
        })
      }
      if (this.retentionRelatedTerms && retental > -1) {
        dispatchEvent(
          new CustomEvent('sanitas_chat-trigger_nudging', {
            detail: { specialCase: 'retention' },
          }),
        )
      }

      this.showLoading = true
      this.hasError = false
      this.searchResults = undefined
      this.searchTerm = value

      console.log(this.searchUrl)
      client()
        .get(this.searchUrl, {
          params: {
            q: value.toLowerCase(),
            _charset_: 'utf-8',
          },
        })
        .then(({ data }) => {
          this.searchResults = data
          this.showLoading = false
          this.hasError = false
        })
        .catch(err => {
          this.showLoading = false
          this.hasError = true
        })
    },
  },
}
</script>
<style lang="postcss" scoped>
.sanitas-search {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  height: 100%;
  z-index: 9999;
  background-color: var(--c-primary-color-1);
  overflow-y: auto;
  margin: 0;
  .closeIcon {
    position: absolute;
    right: var(--spacing-2);
    top: var(--spacing-2);
    cursor: pointer;
    padding: var(--spacing-2);

    & >>> .dotcom-icon {
      width: 20px;
      height: 20px;
    }
  }

  &__form {
    margin-bottom: 35px;
    padding: 15px;
    .search-container {
      padding-top: 25px;
    }

    .title {
      font-weight: 700;
      font-style: normal;
      font-size: 24px;
      line-height: 1.2;
      letter-spacing: 0.25px;
      color: var(--c-primary-neutral-3) !important;
      margin-bottom: 20px;
    }

    .instructions {
      font-weight: 300;
      font-style: normal;
      font-size: 18px;
      line-height: 1.25;
      color: var(--c-primary-neutral-3) !important;
      margin-bottom: 25px;
    }

    .sanitas-form-search {
      position: relative;
      width: 100%;
    }

    .error {
      font-weight: 400;
      font-style: normal;
      font-size: 13px;
      line-height: 1.35;
      letter-spacing: 0.5px;
      color: var(--c-secondary-color-1);

      margin-bottom: 0;
    }
  }
}

@media (--md) {
  .sanitas-search {
    &__form {
      margin: 0 100px;
      .sanitas-form-search {
        width: 70%;
      }
    }
  }
}

@media (--xl) {
  .sanitas-search {
    .sanitas-form-search {
      margin-bottom: 55px;
    }
  }
}

.fade {
  &-enter,
  &-laeve-to {
    opacity: 0;
  }
  &-enter-active,
  &-leave-active {
    transition: opacity 0.6s;
  }
}
</style>
